import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './index';

const ExitOfferModal = ({ isOpen, onClose, onAccept }) => {
    const { t } = useTranslation();
    const highlights = t('landing.exitOffer.highlights', { returnObjects: true }) || [];

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleEscape);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = originalOverflow || 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 px-4 py-8"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose?.();
                }
            }}
        >
            <div
                className="relative w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl shadow-primary-900/20"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-primary-50 via-white to-secondary-100 opacity-90" />
                <div className="relative p-8 sm:p-10">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-5 top-5 text-2xl font-semibold text-gray-400 transition hover:text-gray-600"
                        aria-label={t('common.close')}
                    >
                        ×
                    </button>

                    <div className="space-y-6 text-center sm:text-left">
                        <span className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                            {t('landing.exitOffer.badge')}
                        </span>
                        <div className="space-y-3">
                            <h2 className="text-3xl font-black text-gray-900 sm:text-4xl">
                                {t('landing.exitOffer.title')}
                            </h2>
                            <p className="text-lg font-semibold text-primary-700 sm:text-xl">
                                {t('landing.exitOffer.subtitle')}
                            </p>
                            <p className="text-base text-gray-700 sm:text-lg">
                                {t('landing.exitOffer.description')}
                            </p>
                        </div>

                        {highlights.length > 0 && (
                            <div className="rounded-3xl border border-primary-100/80 bg-white/90 p-6 backdrop-blur">
                                <h3 className="text-xs font-semibold uppercase tracking-[0.35em] text-primary-600 sm:text-sm">
                                    {t('landing.exitOffer.highlightsTitle')}
                                </h3>
                                <ul className="mt-4 space-y-3 text-sm text-gray-700 sm:text-base">
                                    {highlights.map((item, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600">
                                                {index + 1}
                                            </span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p className="text-xs text-gray-500 sm:text-sm">
                            {t('landing.exitOffer.terms')}
                        </p>

                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                className="w-full sm:w-auto"
                            >
                                {t('landing.exitOffer.secondaryCta')}
                            </Button>
                            <a
                                href="https://buy.stripe.com/aFa00jazjeNhgPh7SR3Je02?prefilled_promo_code=WELCOME35"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors text-center"
                            >
                                {t('landing.exitOffer.primaryCta')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExitOfferModal;


